import { Clear, FilterList } from '@mui/icons-material';
import { Box, Button, Checkbox, Chip, Divider, MenuItem, MenuList, Popover, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { splitOptionCount } from '@sophocles/components/FilterBar/filterOptionLabel';
import {
  NoOptions,
  OPTION_LIST_MAX_WIDTH,
  OptionCount,
  OptionLabel,
} from '@sophocles/data-grids/components/filters/FilterOptionRow';
import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';
import { OptionMark } from '@sophocles/data-grids/grid/descriptorIcons';

function getDefaultLabel(option) {
  return option?.label ?? option?.value ?? '';
}

export default function DataGridMultiSelectFilter({
  label,
  values = [],
  onChange,
  options = [],
  disabled = false,
  popoverTitle = null,
  getOptionLabel = getDefaultLabel,
  icon: iconProp,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const valueSet = useMemo(() => new Set(values), [values]);
  const selectedCount = values.length;
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggle = useCallback(
    optionValue => {
      const nextValues = valueSet.has(optionValue)
        ? values.filter(value => value !== optionValue)
        : [...values, optionValue];
      onChange(nextValues);
    },
    [onChange, valueSet, values],
  );

  const handleClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const resolvedPopoverTitle = popoverTitle ?? label;
  const resolvedChipLabel = selectedCount > 0 ? `${label} (${selectedCount})` : label;

  return (
    <>
      <Chip
        icon={iconProp || <FilterList sx={{ fontSize: 16 }} />}
        label={resolvedChipLabel}
        title={typeof resolvedChipLabel === 'string' ? resolvedChipLabel : undefined}
        size="small"
        variant={selectedCount > 0 ? 'filled' : 'outlined'}
        disabled={disabled}
        onClick={handleOpen}
        sx={toolbarChipSx}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 260, maxWidth: OPTION_LIST_MAX_WIDTH, maxHeight: 420 } } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {resolvedPopoverTitle}
          </Typography>
          <Button
            size="small"
            color="inherit"
            startIcon={<Clear fontSize="small" />}
            onClick={handleClear}
            disabled={selectedCount === 0}
          >
            Clear
          </Button>
        </Box>
        <Divider />
        {options.length === 0 ? (
          <NoOptions label={label} />
        ) : (
          <MenuList dense sx={{ py: 0.5 }}>
            {options.map(option => {
              const { label, count } = splitOptionCount({ ...option, label: getOptionLabel(option) });
              return (
                <MenuItem key={option.value} disabled={option.disabled} onClick={() => handleToggle(option.value)}>
                  <Checkbox edge="start" size="small" checked={valueSet.has(option.value)} disableRipple />
                  <OptionMark icon={option.icon} tone={option.tone} />
                  <OptionLabel label={label} />
                  <OptionCount count={count} />
                </MenuItem>
              );
            })}
          </MenuList>
        )}
      </Popover>
    </>
  );
}
